"use client";

// AFF-04: Click report must be scoped by affiliate_id = current user's _id
// Angular: ClickReportListComponent — searchcondition: { affiliate_id: userId }

import { useState, useCallback } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { MousePointerClick, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, DataTableColumnHeader, type DataTableConfig } from "@admin/_components/data-table";
import { useApiList } from "@admin/_lib/api-hooks";

interface ClickRow {
  _id: string;
  name?: string;
  email?: string;
  click_count?: number;
  ip_address?: string;
  created_at?: number;
  createdon_datetime?: number;
  affiliate_id?: string;
}

export default function AffiliateClicksPage() {
  const router = useRouter();
  const { data: session } = useSession();
  const userId = (session?.user as unknown as Record<string, unknown>)?._id as string | undefined;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("createdon_datetime");
  const [sortType, setSortType] = useState<"asc" | "desc">("desc");
  const [searchCondition, setSearchCondition] = useState<Record<string, unknown>>({});

  const { data, isLoading, isFetching } = useApiList<ClickRow>("user/List-for-click-conversion", {
    page,
    limit,
    sortField,
    sortType,
    condition: {},
    searchcondition: {
      ...searchCondition,
      ...(userId ? { affiliate_id: userId } : {}),
    },
  });

  const handleSearch = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) { const next = { ...prev }; delete next[field]; return next; }
      return { ...prev, [field]: { $regex: value, $options: "i" } };
    });
  }, []);

  const handleSort = useCallback((field: string, type: "asc" | "desc") => {
    setSortField(field);
    setSortType(type);
  }, []);

  const columns: ColumnDef<ClickRow>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      enableSorting: true,
    },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "click_count",
      header: "Clicks",
      cell: ({ row }) => (
        <span className="font-semibold" style={{ color: "#ffa800" }}>
          {row.original.click_count ?? 0}
        </span>
      ),
    },
    { accessorKey: "ip_address", header: "IP Address" },
    {
      id: "created_at",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      enableSorting: true,
      accessorFn: (row) => row.createdon_datetime ?? row.created_at,
      cell: ({ row }) => {
        const val = row.original.createdon_datetime ?? row.original.created_at;
        return val
          ? <span className="text-xs text-muted-foreground">{format(new Date(val), "MMM dd, yyyy")}</span>
          : "—";
      },
    },
  ];

  const tableConfig: DataTableConfig = {
    searchFields: [
      { label: "Name",  field: "name",  placeholder: "Search by name..."  },
      { label: "Email", field: "email", placeholder: "Search by email..." },
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <MousePointerClick className="size-4" />
            Click Report
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Track affiliate link clicks and visitor activity</p>
        </div>
      </div>

      <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
        <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            Click Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 relative">
          {isFetching && !isLoading && (
            <div className="absolute top-2 right-4 z-10">
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading clicks...
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              totalCount={data?.count ?? 0}
              config={tableConfig}
              page={page}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearch={handleSearch}
              onSort={handleSort}
              onFilter={() => {}}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
